import * as THREE from 'three';
import { clamp, fbm, hash01, hash3, lerp, mixRgb, mulberry32, smoothstep } from '@/core/rng';
import { surfaceHeight } from '@/world/chunk';
import { LIFT_BYTE_SCALE } from '@/world/farField';
import { BIOMES, type BiomeKey, type Site } from '@/world/types';
import type { Region } from '@/world/region';
import type { VegetationKit } from './vegetation';
import { LEAN } from './chunkView';

/**
 * The island's forest, everywhere the chunk streamer isn't.
 *
 * Chunks grow plants out to LOD 2 and no further, so past the streamed ring the
 * world was bald: a coarse green heightfield with rocks on it and not one tree,
 * which is exactly the "smooth dough" that pulling the camera back used to
 * reveal. This is the vegetation counterpart of `horizon.ts` — one region-scale
 * layer, built once from the immutable Region, carrying the trees the streamer
 * cannot reach, with a moving per-cell hole punched under the mounted chunks so
 * near forest and far forest never stand in the same place.
 *
 * It agrees with the near forest about where forest belongs because it reads the
 * same fields: the biome's own density, its rainfall, its drainage, and the same
 * two-scale stand field that gives a hillside a treeline. Only the big kinds
 * stand here — a shrub and a tuft are sub-pixel at this range and would cost an
 * instance to draw nothing.
 *
 * Two draw calls for the whole 50 km island. A chunk's vegetation costs one call
 * per plant kind standing in it and the streamed ring already spends a thousand
 * of them, so the layer that covers the other forty-five kilometres has to be
 * free at the call level; what it does cost is vertices, and `CAP` explains what
 * that buys.
 *
 * Deterministic and pure: placement is `mulberry32(hash3(cellX, cellZ, seed))`
 * over the region grid, so the same island always carries the same far forest.
 * `update` reads the camera's orbit radius and nothing else.
 */

export interface FarCanopy {
  group: THREE.Group;
  /** Cells per side of the occupancy window — horizon.ts's convention. */
  readonly holeCells: number;
  /** Trees standing on the island, both kinds. Diagnostics only. */
  readonly instances: number;
  /**
   * Cut the hole per cell: `occupancy` is holeCells² bytes, row-major from
   * minimum z, 255 where the streamer has a chunk that grows its own plants.
   * `originX/Z` is the world corner of the window's first cell, `cellSize` the
   * chunk size in metres.
   */
  setHole(originX: number, originZ: number, cellSize: number, occupancy: Uint8Array): void;
  /**
   * The island-wide history field, `n * n * 4` RGBA bytes: the sere's recovery
   * region.n per side over the region's extent. A far tree taller than the
   * ceiling under it collapses, which is `applySere`'s omission rule carried
   * past the streamed ring: a burn is a hole in the canopy for decades, from
   * any altitude, without one instance rebuilt.
   */
  setField(rgb: Uint8Array): void;
  /**
   * Camera orbit radius in metres; sets how much of the canopy draws, and how
   * big. `densityMul` scales the drawn share — the settings menu's "far
   * forest" knob; 0 empties the layer, 1 is the shipped tuning.
   */
  update(camR: number, densityMul?: number): void;
  dispose(): void;
}

/** ±24 cells around the focus — the widest ring the streamer ever wants. */
const HOLE_CELLS = 49;

/**
 * Instance budget per kind for the whole island, and the measurement behind it.
 *
 * The frame is vertex bound here, not fragment bound and not draw-call bound:
 * sixty thousand of these trees cost the same 6 ms whether they are on screen or
 * sunk two hundred kilometres below the world where every fragment is clipped.
 * At 51 and 60 triangles a shell that works out around 0.15 ms per thousand
 * instances, and the streamed ring next door has already spent most of the
 * frame — so the honest ceiling is a few tens of thousands drawn, and `update`
 * spends them where they show. (A dedicated low-poly far shell would buy about
 * four times as many for the same milliseconds; these are the kit's own shapes
 * on purpose, so a far crown and a near crown are the same crown.)
 */
const CAP = 60000;
/** Placement attempts in a cell that is solid forest; thin ground gets fewer. */
const ATTEMPTS = 95;
/** Below this a stand is scattered specks, not forest — not worth the instances. */
const FOREST_FLOOR = 0.06;
/** Metres of tree. Anything shorter is understory, and understory does not read here. */
const MIN_HEIGHT = 6;
/** Metres above the sea a cell needs before it can grow a far tree. */
const SEA_CLEAR = 2;

/** The horizon mesh's own lattice, so a far tree stands on the ground it is drawn over. */
const HORIZON_RES = 320;
/** horizon.ts sits its mesh this far down, strictly under the chunk skirt line. */
const HORIZON_DROP = 1.5;

/**
 * How much of the canopy draws, and how wide its crowns are, by orbit radius.
 *
 * Close in the streamed ring covers four kilometres of ground and this layer is
 * a band beyond it, most of the way into the fog — worth having, not worth many
 * vertices, and its crowns must stay near the size the near forest's are or the
 * seam reads as a step. Pulled out is the opposite case in every respect: the
 * ring has just given back a thousand draw calls, this layer *is* the forest on
 * nine tenths of the screen, and a crown there is one or two pixels, so it can
 * afford almost all of itself and can trade shape fidelity for coverage.
 *
 * The whole transition rides two uniforms and an instance count, so it costs
 * nothing per frame — and because an instance that stops being drawn has already
 * shrunk to nothing (see FADE), it never pops.
 */
const NEAR_R = 700, FAR_R = 6500;
const BOOST_NEAR = 1.45, BOOST_FAR = 2.75;
const CUT_NEAR = 0.36, CUT_FAR = 0.52;
/** Share of the ranked canopy that shrinks away rather than blinking out. */
const FADE = 0.07;

/**
 * The woody shares of `chunk.ts`'s KIND_MIX, folded to the two shapes this
 * shell draws. Deliberately a copy: this is render-side and that is
 * generation-side, and they must agree — if these columns drift, the far
 * forest changes species where the near forest does not, and the seam shows a
 * line of the wrong tree. The fold: broad-crowned kinds (broadleaf, spreading,
 * palm, willow) sum into the first column, spired kinds (conifer, columnar)
 * into the second — at three kilometres a willow is a broad blob and a poplar
 * is a spire, and that is all the range can resolve anyway. Snags are omitted:
 * a bare trunk is sub-pixel here.
 *
 * **Re-derived cell by cell against KIND_MIX, and two rows had drifted.** A
 * comment saying "these must agree" is not a mechanism, and both halves of
 * this file's own argument were live counterexamples: `conifer`'s spire share
 * lagged the 0.74 → 0.71 the v0.5 pass made in `chunk.ts`, and `alpine` was
 * carrying 0.02 broad against a row that lost its `broadleaf` **entirely** in
 * v0.4 plus 0.12 spire against 0.05 — the older of the two drifts had
 * survived a whole release putting far-canopy broad crowns on ground the near
 * forest grows none on. Every one of the twelve rows below is now the exact
 * fold of the same row of `KIND_MIX`; if you touch a woody column there,
 * re-add it here, and the ROWS to check are all of them, not the one you
 * edited.
 */
export const WOODY_MIX: Record<BiomeKey, readonly [number, number]> = {
  ocean:     [0.00, 0.00],
  beach:     [0.13, 0.00],
  marsh:     [0.10, 0.02],
  riparian:  [0.66, 0.09],
  redwood:   [0.10, 0.72],
  oak:       [0.69, 0.04],
  chaparral: [0.03, 0.00],
  grassland: [0.06, 0.02],
  scrub:     [0.03, 0.01],
  desert:    [0.03, 0.00],
  conifer:   [0.07, 0.71],
  alpine:    [0.00, 0.05],
};

/**
 * `chunk.ts`'s `soilAt`, copied for the same reason as WOODY_MIX: the near
 * forest reads its biome through this jitter, so cell borders dissolve into
 * wavy blends instead of rectangles. Reading the raw grid here instead would
 * paint the far forest's species in 260 m squares.
 */
function soilAt(region: Region, wx: number, wz: number, trueSite: Site): Site {
  // chunk.ts moved to interpolated fbm (and then grew the 700 m ecotone
  // warp); this copy had kept the old block-hash jitter, so the far forest's
  // species were already drifting from the near forest's at the seam. Now
  // byte-for-byte the same reads, seeded the same.
  const s0 = region.seed;
  const jx = (fbm(wx / 700, wz / 700, 2, s0 + 61) - 0.5) * 520
           + (fbm(wx / 150, wz / 150, 2, s0 + 51) - 0.5) * 300
           + (fbm(wx / 40, wz / 40, 2, s0 + 53) - 0.5) * 110;
  const jz = (fbm(wx / 700, wz / 700, 2, s0 + 67) - 0.5) * 520
           + (fbm(wx / 150, wz / 150, 2, s0 + 57) - 0.5) * 300
           + (fbm(wx / 40, wz / 40, 2, s0 + 59) - 0.5) * 110;
  const s = region.siteAt(wx + jx, wz + jz);
  return s.land === trueSite.land ? s : trueSite;
}

/** `chunk.ts`'s `standField` — where forest wants to be, and where its glades are. */
function standField(region: Region, wx: number, wz: number): number {
  const s = region.seed;
  const stand = fbm(wx / 1200, wz / 1200, 3, s + 401);
  const grain = fbm(wx / 300, wz / 300, 3, s + 409);
  return clamp(smoothstep(0.36, 0.60, stand) * (0.32 + 0.85 * smoothstep(0.24, 0.58, grain)), 0, 1);
}

interface FarTree {
  x: number; y: number; z: number;
  /**
   * Width and height as SCALES on the kit's broadleaf / conifer build, in the
   * same units `Instance.scaleXZ` / `scaleY` use — those two builds happen to
   * stand 0.98 and 1.00 tall, so here the numbers are within 2% of metres.
   * That is a fact about those two kinds and not a property of the kit; see
   * `world/chunk.ts`'s `Instance.scaleY`.
   */
  w: number; h: number;
  rot: number;
  r: number; g: number; b: number;
  conifer: boolean;
  /** Hashed importance in [0, 1). A prefix of the sorted set is an even thinning. */
  rank: number;
}

export function buildFarCanopy(region: Region, kit: VegetationKit): FarCanopy {
  const { n, cell, seed } = region;
  const half = (n / 2) * cell;
  const centre = (i: number) => (i + 0.5) * cell - half;
  const peak = region.maxHeight * region.heightScale;

  // The horizon mesh interpolates the ground over 156 m spans, so on a ridge its
  // surface runs metres below the true one. Seating a tree on the true surface
  // there leaves it hovering; seating every tree on the *lower* of the two costs
  // a buried foot at worst, and a buried foot is invisible at three kilometres
  // while a floating tree is not.
  const ext = region.extentMetres;
  const hStep = ext / (HORIZON_RES - 1);
  const lattice = new Float32Array(HORIZON_RES * HORIZON_RES);
  for (let j = 0; j < HORIZON_RES; j++) for (let i = 0; i < HORIZON_RES; i++)
    lattice[j * HORIZON_RES + i] =
      surfaceHeight(region, -half + i * hStep, -half + j * hStep, 0);
  const latticeAt = (wx: number, wz: number): number => {
    const fx = clamp((wx + half) / hStep, 0, HORIZON_RES - 1.001);
    const fz = clamp((wz + half) / hStep, 0, HORIZON_RES - 1.001);
    const i = fx | 0, j = fz | 0, u = fx - i, v = fz - j;
    const a = lattice[j * HORIZON_RES + i], b = lattice[j * HORIZON_RES + i + 1];
    const c = lattice[(j + 1) * HORIZON_RES + i], d = lattice[(j + 1) * HORIZON_RES + i + 1];
    return lerp(lerp(a, b, u), lerp(c, d, u), v);
  };

  // ---- placement: walk the region grid, plant the cells that grow forest ----
  const trees: FarTree[] = [];
  for (let cz = 0; cz < n; cz++) for (let cx = 0; cx < n; cx++) {
    const wx = centre(cx), wz = centre(cz);
    const cellSite = region.siteAt(wx, wz);
    if (!cellSite.land || cellSite.height < SEA_CLEAR) continue;
    const cellBiome = BIOMES[cellSite.biome];
    const woody = WOODY_MIX[cellSite.biome];
    if (cellBiome.density < 0.08 || woody[0] + woody[1] < 0.03) continue;

    // How hard to try here: the cell's own forest, so a closed stand is worked
    // over and a thin shoulder is sampled a few times. The *survival* test below
    // still reads the stand field per point, so edges stay feathered.
    const seedField = standField(region, wx, wz);
    const attempts = 3 + Math.round((ATTEMPTS - 3)
      * clamp(cellBiome.density, 0, 1) * (0.30 + 0.80 * seedField));
    const rnd = mulberry32(hash3(cx, cz, seed + 0x2f0e));

    for (let k = 0; k < attempts; k++) {
      // every draw happens before any filter, so the stream cannot know what a
      // reject was going to be — the chunk-side discipline, for the same reason
      const px = wx + (rnd() - 0.5) * cell, pz = wz + (rnd() - 0.5) * cell;
      const live = rnd();
      const pick = rnd();
      const grow = rnd(), wide = rnd(), tall = rnd(), tone = rnd();
      const rot = rnd() * Math.PI * 2;
      const vigour = 0.6 + rnd() * 0.8;
      const shade = 0.9 + rnd() * 0.36;

      const trueSite = region.siteAt(px, pz);
      if (!trueSite.land) continue;
      const site = soilAt(region, px, pz, trueSite);
      if (!site.land) continue;
      const b = BIOMES[site.biome];
      const m = WOODY_MIX[site.biome];
      const wet = clamp(Math.log(1 + site.flow) / 9, 0, 1);
      const canopy = standField(region, px, pz);

      // chunk.ts's density, to the letter — the far forest thickens and thins
      // with the near one because it is reading the same numbers
      let density = b.density * (0.5 + 0.8 * clamp(site.rain * 1.3, 0, 1) + wet * 0.5);
      density *= lerp(b.woody ? 0.06 : 0.40, 1.15, canopy);
      if (density < FOREST_FLOOR || live > clamp(density, 0, 1)) continue;

      const rel = clamp(site.height / peak, 0, 1);
      const alpine = smoothstep(0.30, 0.75, rel);
      const dry = clamp(0.34 - site.rain, 0, 1) * 2;
      const mixBroad = m[0] * (1 - alpine * 0.7) * (1 - dry * 0.5) + wet * 0.22;
      const mixConif = m[1] * (1 + alpine * 1.6) * (1 - wet * 0.4);
      if (mixBroad + mixConif <= 0) continue;
      const conifer = pick * (mixBroad + mixConif) >= mixBroad;

      const stature = clamp(
        0.34 + b.density * 0.44 + clamp(site.rain * 1.3, 0, 1) * 0.42 + wet * 0.22,
        0.34, 1.08) * (1 - alpine * 0.55);

      // the same size laws the near forest uses, drawn from the upper half of
      // the range: a sapling costs an instance and draws nothing out here
      const big = 0.45 + grow * 0.55;
      const emergent = tall < (conifer ? 0.22 : 0.18);
      const h = conifer
        ? (emergent ? 15 + big * 11 : 8 + big * 9) * vigour * stature
        : (emergent ? 10 + big * 8 : 4.5 + big * 5) * vigour * stature;
      if (h < MIN_HEIGHT) continue;
      // the same ratio laws `chunk.ts` uses, to the digit — and they WIDENED
      // in v0.5 (conifer 0.20+0.14 → 0.15+0.26, broadleaf 0.78+0.34 →
      // 0.58+0.80). If this line does not move with that one the near forest
      // and the far forest wear different crown proportions and the streamed
      // ring's edge reads as a change of species, which is the same class of
      // bug `soilAt` and `WOODY_MIX` are copied here to avoid.
      const ratio = conifer ? 0.15 + wide * 0.26 : 0.58 + wide * 0.80;

      let col = mixRgb(b.colour[0], b.colour[1], tone);
      col = mixRgb(col, [66, 100, 62], clamp(site.rain * 1.5 - 0.42, 0, 1) * 0.62);
      col = mixRgb(col, [52, 86, 56], wet * 0.42);
      col = mixRgb(col, [126, 120, 78], (1 - vigour) * 0.45);
      col = conifer ? mixRgb(col, [34, 62, 52], 0.42) : mixRgb(col, [86, 112, 56], 0.26);

      // Seat under BOTH surfaces the tree might stand on — but never bury more
      // than a quarter of the tree. The unconditional min() sat every tree
      // under the 156 m horizon lattice, which runs many metres below the true
      // surface on convex slopes; inside the streamed ring the drawn ground is
      // LOD-3 chunk relief up near the true height, so those trees showed only
      // their crown tips — rows of dark dashes following the contours. The cap
      // trades that for a worst-case ~one-pixel float at horizon ranges.
      const sh0 = surfaceHeight(region, px, pz, 0);
      const ground = Math.max(Math.min(sh0, latticeAt(px, pz) - HORIZON_DROP),
                              sh0 - (2 + 0.25 * h));
      trees.push({
        x: px, z: pz, y: ground - 0.6 - h * 0.03,
        w: h * ratio, h, rot,
        r: col[0] / 255 * shade, g: col[1] / 255 * shade, b: col[2] / 255 * shade,
        conifer,
        rank: hash01(Math.round(px), Math.round(pz), seed + 0x77c1),
      });
    }
  }

  // Rank order, so drawing only the first `count` instances thins the whole
  // island evenly instead of clipping a corner off it. That is what lets `update`
  // trade density for frame time with one integer and no visible seam.
  trees.sort((a, b) => a.rank - b.rank);

  // ---- one material: the kit's own, plus the hole and the range uniforms ----
  const holeData = new Uint8Array(HOLE_CELLS * HOLE_CELLS);
  const holeTex = new THREE.DataTexture(holeData, HOLE_CELLS, HOLE_CELLS, THREE.RedFormat);
  // Linear, not nearest: sampled bilinearly the occupancy becomes a ~one-cell
  // feather, and the shader scales trees by it — so the canopy thins out over
  // 256 m instead of ending on a cell-aligned straight edge. The hard step
  // drew the streamer's own grid onto the forest as dashed lines that crawled
  // whenever the ring moved.
  holeTex.magFilter = holeTex.minFilter = THREE.LinearFilter;
  holeTex.wrapS = holeTex.wrapT = THREE.ClampToEdgeWrapping;
  const uHoleWin = { value: new THREE.Vector3(0, 0, 0) };   // origin x, z, 1/span
  const uBoost = { value: BOOST_NEAR };
  const uCut = { value: CUT_NEAR };

  // the sere's ceiling field: fixed island window, linear-filtered so the
  // kill edge feathers inside one 260 m texel instead of stepping on it —
  // 255 (climax) lerping toward a burned texel's low byte crosses any real
  // tree height only in the last stretch, so the blur never eats live forest
  // Three channels now, not one: R the sere's ceiling as before, G the land's
  // remembered biomass and B how far a god has moved the ground. One texture,
  // one sample, and the two stores that used to stop at the streamed ring ride
  // along. See `world/farField.ts` for the encoding and for why a blessing is
  // allowed to do nothing here (law 8: a coarse LOD may only omit).
  // RGBA rather than RGB: three.js dropped RGBFormat, and the alpha channel
  // is simply unused rather than pretending to mean something.
  const sereData = new Uint8Array(n * n * 4);
  for (let k = 0; k < n * n; k++) {
    sereData[k * 4] = 255; sereData[k * 4 + 1] = 128;
    sereData[k * 4 + 2] = 128; sereData[k * 4 + 3] = 255;
  }
  const sereTex = new THREE.DataTexture(sereData, n, n, THREE.RGBAFormat);
  sereTex.magFilter = sereTex.minFilter = THREE.LinearFilter;
  sereTex.wrapS = sereTex.wrapT = THREE.ClampToEdgeWrapping;
  sereTex.needsUpdate = true;
  const uSereWin = { value: new THREE.Vector3(-half, -half, 1 / (n * cell)) };

  // A clone, never the shared material: mutating the kit's would put the hole
  // into every chunk's vegetation too. The clone keeps the kit's parameters and
  // *calls the kit's own onBeforeCompile*, so the far trees ride the identical
  // wind uniform — a far crown and the near crown beside it sway in phase.
  const kitCompile = kit.material.onBeforeCompile;
  const mat = kit.material.clone();
  mat.onBeforeCompile = (sh, renderer) => {
    kitCompile.call(mat, sh, renderer);
    sh.uniforms.uHoleTex = { value: holeTex };
    sh.uniforms.uHoleWin = uHoleWin;
    sh.uniforms.uBoost = uBoost;
    sh.uniforms.uCut = uCut;
    sh.uniforms.uSereTex = { value: sereTex };
    sh.uniforms.uSereWin = uSereWin;
    // The kill is per instance, in the vertex shader, rather than per fragment
    // as in horizon.ts: a tree is atomic — it is either in a mounted cell or it
    // is not — and collapsing it to its own base point costs no fragments at
    // all. Same occupancy texture, same window uniform, same convention.
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>
        uniform sampler2D uHoleTex;
        uniform vec3 uHoleWin;
        uniform float uBoost;
        uniform float uCut;
        uniform sampler2D uSereTex;
        uniform vec3 uSereWin;
        attribute float frank;`)
      .replace('#include <project_vertex>', `
        {
          vec3 fcW = (modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0)).xyz;
          vec2 huv = (fcW.xz - uHoleWin.xy) * uHoleWin.z;
          float mounted = 0.0;
          if (huv.x > 0.0 && huv.x < 1.0 && huv.y > 0.0 && huv.y < 1.0)
            mounted = texture2D(uHoleTex, huv).r;
          // uCut is where full size ends; the next FADE of the ranked set shrinks
          // to nothing, and update() draws exactly that far and no further, so
          // an instance that stops being drawn was already zero-sized
          float keep = 1.0 - smoothstep(uCut, uCut + ${FADE.toFixed(3)}, frank);
          vec2 suv = (fcW.xz - uSereWin.xy) * uSereWin.z;
          vec3 hist = texture2D(uSereTex, suv).rgb;
          float treeH = length(instanceMatrix[1].xyz);
          // the sere's omission rule, per instance: taller than what could
          // have regrown here means the burn took this tree decades ago
          float ceilM = hist.r * 255.0;
          float regrown = step(treeH, ceilM + 0.5);
          // and the land's own memory: hard withering strips the far forest
          // exactly as applyEnv clears the understory under -0.45 in a chunk.
          // A BLESSING does nothing here on purpose — a coarse LOD may only
          // omit, never invent trees the near view would then disagree with.
          float bio = (hist.g * 255.0 - 128.0) / 127.0;
          float withered = 1.0 - smoothstep(0.35, 0.85, -bio);
          transformed *= uBoost * keep * (1.0 - mounted) * regrown * withered;
          // and it stands on whatever ground there is now: moving a tree with
          // its own hill is what applyEarth already does per chunk, so the far
          // canopy has to match or a raised hill buries its distant trees
          float lift = (hist.b * 255.0 - 128.0) * ${LIFT_BYTE_SCALE.toFixed(3)};
          transformed.y += lift / max(treeH, 0.001);
        }
        #include <project_vertex>`);
  };

  // ---- two meshes for the island, filled in rank order ----
  // Two, not one per region tile: tiles were tried, and with a 400 km far plane
  // and a 46° field of view the frustum swallows the whole island — 62 of 64
  // tiles passed the test at every distance. The cull bought nothing and the
  // draw calls were real, so the island is one mesh per kind and the cost is
  // governed by `update`'s instance count instead.
  const kinds: { conifer: boolean; src: THREE.BufferGeometry; list: FarTree[] }[] = [
    { conifer: false, src: kit.geometry.broadleaf, list: [] },
    { conifer: true, src: kit.geometry.conifer, list: [] },
  ];
  for (const t of trees) {
    const bucket = kinds[t.conifer ? 1 : 0].list;
    if (bucket.length < CAP) bucket.push(t);
  }

  const _m = new THREE.Matrix4(), _q = new THREE.Quaternion();
  const _p = new THREE.Vector3(), _s = new THREE.Vector3(), _c = new THREE.Color();
  const _tilt = new THREE.Quaternion(), _axis = new THREE.Vector3();
  const UP = new THREE.Vector3(0, 1, 0);
  /**
   * `chunkView.ts`'s lean, on this side of the seam — the same two hashes with
   * the same salts, so the far forest tips off plumb by the same distribution
   * the near forest does.
   *
   * It is the fourth thing in this file kept deliberately in step with the
   * near tier (`soilAt`, `WOODY_MIX`, the ratio law), and it is here for the
   * reason the other three are: a property that stops at the streamed ring's
   * edge is a property the edge SHOWS.
   *
   * This one is small, and saying how small is more use than defending it. On
   * `tools/foliage.ts`'s stage (46°, 1080 px) a 12 m broadleaf is 15.3 px at
   * 1 km and 5.1 px at 3 km, so `LEAN.broadleaf` at its maximum displaces the
   * crown by 0.84 px and 0.28 px respectively — sub-pixel across the whole
   * range this shell draws. It is bought for the rule, not for the picture.
   * What it is not is a rule with a hole in it, and `test/visual-kits.ts`
   * asserts it because nothing on screen would report the regression.
   */
  const lean = (t: FarTree): number =>
    hash01(Math.round(t.x * 3), Math.round(t.z * 3), 0x1ea4)
    * LEAN[t.conifer ? 'conifer' : 'broadleaf'];
  const leanDir = (t: FarTree): number =>
    hash01(Math.round(t.z * 3), Math.round(t.x * 3), 0x0d19) * Math.PI * 2;
  const group = new THREE.Group();
  group.name = 'farCanopy';
  const meshes: THREE.InstancedMesh[] = [];
  let instances = 0;

  for (const { conifer, src, list } of kinds) {
    if (!list.length) continue;
    // The kit's shape, but on a private copy of the buffers: `frank` is an
    // instanced attribute, and hanging one off the geometry every chunk shares
    // would put a stray per-instance buffer on all of their vegetation too.
    // Position, normal, colour and the wind's `flex` all come across.
    const geo = src.clone();
    const frank = new THREE.InstancedBufferAttribute(new Float32Array(list.length), 1);
    geo.setAttribute('frank', frank);
    const im = new THREE.InstancedMesh(geo, mat, list.length);
    im.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(list.length * 3), 3);
    im.name = `far-${conifer ? 'conifer' : 'broadleaf'}`;
    // one mesh spans the island, so its bounds are the island: culling by them
    // can only ever throw the whole forest away at the wrong moment
    im.frustumCulled = false;
    im.receiveShadow = true;
    for (let i = 0; i < list.length; i++) {
      const t = list[i];
      _q.setFromAxisAngle(UP, t.rot);
      const a = leanDir(t);
      _q.multiply(_tilt.setFromAxisAngle(
        _axis.set(Math.cos(a), 0, Math.sin(a)), lean(t)));
      _m.compose(_p.set(t.x, t.y, t.z), _q, _s.set(t.w, t.h, t.w));
      im.setMatrixAt(i, _m);
      im.setColorAt(i, _c.setRGB(t.r, t.g, t.b));
      // index-space rank, so `count = ceil(cut * total)` drops exactly the
      // instances the shader has already shrunk to nothing
      frank.setX(i, i / list.length);
    }
    im.instanceMatrix.needsUpdate = true;
    if (im.instanceColor) im.instanceColor.needsUpdate = true;
    frank.needsUpdate = true;
    meshes.push(im);
    group.add(im);
    instances += list.length;
  }

  return {
    group,
    holeCells: HOLE_CELLS,
    instances,
    setHole(originX, originZ, cellSize, occupancy) {
      holeData.set(occupancy);
      holeTex.needsUpdate = true;
      uHoleWin.value.set(originX, originZ, 1 / (HOLE_CELLS * cellSize));
    },
    setField(rgb) {
      sereData.set(rgb);
      sereTex.needsUpdate = true;
    },
    update(camR, densityMul = 1) {
      const t = smoothstep(NEAR_R, FAR_R, camR);
      uBoost.value = lerp(BOOST_NEAR, BOOST_FAR, t);
      const cut = clamp(lerp(CUT_NEAR, CUT_FAR, t) * densityMul, 0, 1);
      uCut.value = cut;
      for (const im of meshes) {
        const total = im.instanceMatrix.count;
        im.count = Math.min(total, Math.ceil((cut + FADE) * total));
      }
    },
    dispose() {
      for (const im of meshes) {
        group.remove(im);
        im.geometry.dispose();   // our own clone; the kit's originals are untouched
        im.dispose();
      }
      meshes.length = 0;
      mat.dispose();
      holeTex.dispose();
      sereTex.dispose();
    },
  };
}
